/**
 * Seeded roles (AUTH_RBAC_ARCHITECTURE §3.4). Roles are data: administrators may change them later.
 * `externalRoleKey` records the field-app role a role mirrors (Ops /ops_access roles, Fleet users.role).
 * Blueprint Appendix A role families are all represented.
 */
import { PERMISSIONS, type PermissionCode } from './permissions';

export interface RoleDef {
  readonly code: string;
  readonly name: string;
  readonly group: 'Administration' | 'Operations' | 'Farm' | 'Plant' | 'Quality' | 'Logistics' | 'Delivery' | 'Customers' | 'Finance' | 'Workforce' | 'Governance' | 'Service';
  readonly description: string;
  readonly externalRoleKey?: string;
  readonly requiresMfa: boolean;
  readonly permissions: readonly PermissionCode[];
}

const ALL = PERMISSIONS.map((perm) => perm.code) as PermissionCode[];
const viewsOf = (...modules: string[]): PermissionCode[] =>
  ALL.filter((code) => modules.includes(code.split('.')[0] as string) && /\.view$/.test(code) && code !== 'farm.bill.view');

const BASE: PermissionCode[] = ['notifications.view', 'attention.view'];

export const ROLES: readonly RoleDef[] = [
  {
    code: 'erp_admin',
    name: 'ERP administrator',
    group: 'Administration',
    description: 'Full administration including identity, configuration and integrations.',
    requiresMfa: true,
    permissions: ALL.filter((c) => c !== 'integration.ingest' && c !== 'service.whatsapp.api'),
  },
  {
    code: 'security_admin',
    name: 'Security administrator',
    group: 'Administration',
    description: 'Identity, access, sessions and audit; no business writes.',
    requiresMfa: true,
    permissions: [
      ...BASE,
      'iam.users.view', 'iam.users.manage', 'iam.roles.view', 'iam.roles.manage', 'iam.grants.manage',
      'iam.sessions.view', 'iam.applications.view', 'iam.applications.manage', 'iam.api_clients.view',
      'iam.api_keys.manage', 'audit.view', 'audit.sensitive_access.view', 'system.logs.view', 'config.view',
    ],
  },
  {
    code: 'ops_admin',
    name: 'Operations administrator',
    group: 'Operations',
    description: 'Operational master data (sites, chillers, barrels, vehicles), onboarding approval, stock verification.',
    externalRoleKey: 'ops_admin',
    requiresMfa: true,
    permissions: [
      ...BASE, 'overview.view', 'attention.manage', 'approvals.view', 'config.view',
      'masterdata.view', 'masterdata.locations.manage', 'masterdata.qr.manage', 'masterdata.import',
      'farm.locus.view', 'farm.locus.manage', 'farm.locus.approve', 'farm.locus.kyc.view', 'farm.collection.view',
      'farm.sop.view', 'farm.sop.manage', 'farm.batch.view', 'farm.dispatch.view', 'farm.rate.view',
      'quality.view', 'quality.exception.approve', 'quality.disposition.manage', 'plant.view', 'plant.inward.view', 'inventory.view', 'inventory.verify', 'inventory.loss.manage',
      'assets.view', 'assets.barrel.manage', 'assets.chiller.manage', 'assets.bottle.manage', 'assets.assign', 'assets.maintenance.manage',
      'logistics.vehicles.view', 'logistics.vehicles.manage', 'devices.view', 'integration.view',
      'reports.view', 'analytics.view',
    ],
  },
  {
    code: 'city_manager',
    name: 'City / operations manager',
    group: 'Operations',
    description: 'Oversight of all operations in scoped cities; decides approvals within policy.',
    requiresMfa: true,
    permissions: [
      ...BASE, 'overview.view', 'attention.manage', 'approvals.view', 'tasks.view', 'tasks.manage',
      ...viewsOf('farm', 'quality', 'plant', 'inventory', 'assets', 'delivery', 'logistics', 'customer', 'subscription', 'order', 'bottles', 'service'),
      'farm.locus.approve', 'plant.inward.reconcile', 'delivery.reconcile', 'inventory.adjust',
      'delivery.plan.manage', 'delivery.exception.manage', 'logistics.trips.manage',
      'delivery.correct', 'delivery.reassign', 'delivery.bulk', 'delivery.claims.decide',
      'bottles.custody.correct', 'customer.status.publish', 'datahealth.view',
      'customer.code.allocate', 'order.late_change', 'subscription.manage', 'order.cancel',
      'customer.status.manage', 'customer.subscription.manage', 'customer.discount.manage', 'customer.payment.mark',
      'finance.customer.adjust', 'delivery.office_record', 'customer.day.manage', 'bulk.view', 'bulk.manage',
      'reports.view', 'reports.export', 'analytics.view',
    ],
  },
  {
    code: 'farm_manager',
    name: 'Farm manager (Major Locus)',
    group: 'Farm',
    description: 'Farm operations oversight for granted Major Loci; mirrors the field role.',
    externalRoleKey: 'farm_manager',
    requiresMfa: false,
    permissions: [
      ...BASE, 'farm.locus.view', 'farm.collection.view', 'farm.sop.view', 'farm.batch.view', 'farm.dispatch.view', 'farm.barrel.tare', 'farm.locus.onboard', 'farm.rate.manage', 'farm.bill.view', 'farm.bill.make', 'farm.payment.record',
      'quality.view', 'inventory.view', 'assets.view', 'reports.view',
      // Capture. Inert wherever the field application captures: every command that uses these asks the
      // site's capture authority first (CD-63), so holding them confers nothing at a FIELD_APP site.
      'farm.collection.record', 'farm.batch.manage', 'farm.dispatch.manage', 'quality.test', 'inventory.transfer',
      // Owner direction 2026-09-21 (answers 4 and 12): a failure raises attention; the farm manager may send milk on as an
      // explicit exception, and may request and approve an override at their own site — never their own request.
      'approvals.view', 'quality.exception.forward', 'quality.override', 'quality.approve',
    ],
  },
  {
    code: 'plant_receiving',
    name: 'Plant receiving',
    group: 'Plant',
    description: 'Plant inward view and reconciliation.',
    externalRoleKey: 'plant_receiving',
    requiresMfa: false,
    // `plant.records.declare`: the gate writes arrivals, manual barrels and manual batches. The field rule has always
    // let every plant role write the plant's books at its own plant (ops.rules.json, /plant_records/$plant/$kind).
    permissions: [...BASE, 'plant.view', 'plant.inward.view', 'plant.inward.reconcile', 'plant.inward.receive', 'farm.dispatch.view', 'inventory.view', 'quality.view', 'plant.records.view', 'plant.records.declare'],
  },
  {
    code: 'plant_quality',
    name: 'Plant quality',
    group: 'Plant',
    description: 'Plant quality tests and incidents.',
    externalRoleKey: 'plant_quality',
    requiresMfa: false,
    // `plant.records.declare`: the tester writes a batch's plant QC test (`plant_quality`). Same field rule as above.
    permissions: [...BASE, 'plant.view', 'quality.view', 'quality.test', 'quality.incident.manage', 'quality.hold.manage', 'quality.result.propose', 'quality.exception.forward', 'inventory.view', 'plant.records.view', 'plant.records.declare'],
  },
  {
    code: 'plant_filling',
    name: 'Plant filling',
    group: 'Plant',
    description: 'Filling lots, counts and cold-room transfers.',
    externalRoleKey: 'plant_filling',
    requiresMfa: false,
    permissions: [...BASE, 'plant.view', 'plant.filling.manage', 'plant.coldroom.manage', 'inventory.view', 'inventory.transfer', 'plant.records.view', 'plant.records.declare'],
  },
  {
    code: 'plant_supervisor',
    name: 'Plant supervisor',
    group: 'Plant',
    description: 'Plant oversight, sync-conflict resolution (client decision P-10), wastage and adjustments.',
    externalRoleKey: 'plant_supervisor',
    requiresMfa: true,
    permissions: [
     
      ...BASE, 'attention.manage', 'approvals.view', 'plant.view', 'plant.inward.view', 'plant.inward.reconcile', 'plant.inward.receive',
      'plant.filling.manage', 'plant.production.manage', 'plant.coldroom.manage', 'plant.wastage.record',
      'inventory.view', 'inventory.adjust', 'inventory.verify', 'inventory.transfer', 'inventory.reserve', 'inventory.movement.reverse',
      'quality.view', 'quality.approve', 'quality.hold.manage', 'quality.override', 'quality.exception.forward', 'quality.exception.approve',
      'quality.disposition.manage', 'inventory.loss.manage', 'integration.view', 'integration.resolve', 'reports.view',
      'plant.records.view', 'plant.records.declare', 'plant.records.void', 'plant.dry_count.approve', 'plant.message.send', 'plant.requests.approve',
    ],
  },
  {
    // Plant v3 slice 7 (the owner, 2026-09-28: "void desk se and phone se: manager"). plant_supervisor needs MFA and is
    // refused on phones (CD-90), so the plant's manager signs in to the phone with this role: it may void an entry.
    code: 'plant_manager',
    name: 'Plant manager',
    group: 'Plant',
    description: 'The plant manager on the phone: everything the plant roles declare, and voids an entry with a reason. No MFA, so it may sign in on a phone.',
    externalRoleKey: 'plant_manager',
    requiresMfa: false,
    permissions: [...BASE, 'plant.view', 'plant.inward.view', 'plant.filling.manage', 'plant.coldroom.manage', 'inventory.view', 'quality.view',
      'plant.records.view', 'plant.records.declare', 'plant.records.void', 'plant.dry_count.approve', 'plant.message.send', 'plant.requests.approve',
      'bulk.view'],
  },
  {
    code: 'transit_agent',
    name: 'Transit agent',
    group: 'Plant',
    description: 'Meets the bus at the stand, takes custody of full barrels and hands them over at the plant. Declares custody only; reads no weight, test, stock, price or trace.',
    externalRoleKey: 'transit_agent',
    requiresMfa: false,
    // One permission and nothing else, not even BASE: a notice or an attention item can carry a weight or a
    // failed test, and TRANSIT_AND_CUSTODY §5 gives this role none of either. Granted per plant, like every plant role.
    permissions: ['plant.custody.declare'],
  },
  {
    code: 'quality_head',
    name: 'Quality head',
    group: 'Quality',
    description: 'Quality specifications, thresholds, incidents and overrides (approval).',
    externalRoleKey: 'quality_approver',
    requiresMfa: true,
    permissions: [
      ...BASE, 'attention.manage', 'approvals.view', 'quality.view', 'quality.test', 'quality.approve', 'quality.override',
      'quality.config.manage', 'quality.incident.manage', 'quality.hold.manage', 'quality.result.propose', 'quality.exception.forward',
      'quality.exception.approve', 'quality.disposition.manage', 'farm.batch.view', 'farm.collection.view', 'plant.view',
      'reports.view', 'analytics.view',
    ],
  },
  {
    code: 'hub_custodian',
    name: 'Hub custodian / load person',
    group: 'Logistics',
    description: 'Hub handovers, route loads and returns reconciliation.',
    requiresMfa: false,
    permissions: [...BASE, 'plant.coldroom.manage', 'inventory.view', 'inventory.transfer', 'delivery.view', 'delivery.reconcile', 'bottles.view', 'delivery.record'],
  },
  {
    code: 'delivery_supervisor',
    name: 'Delivery supervisor',
    group: 'Delivery',
    description: 'Delivery operations oversight, field reports, route plans.',
    externalRoleKey: 'admin',
    requiresMfa: false,
    permissions: [
      ...BASE, 'attention.manage', 'delivery.view', 'delivery.reconcile', 'delivery.reports.manage', 'bottles.view',
      'delivery.plan.manage', 'delivery.record', 'delivery.pod.view', 'delivery.qr.bypass', 'delivery.exception.manage',
      'delivery.correct', 'delivery.reassign', 'delivery.bulk', 'delivery.claims.decide', 'bottles.custody.correct',
      'delivery.riders.manage',
      'datahealth.view',
      'logistics.routes.view', 'logistics.routes.manage', 'logistics.trips.view', 'logistics.trips.manage',
      'logistics.vehicles.view',
      'customer.view', 'subscription.view', 'order.view', 'attendance.view', 'reports.view',
    ],
  },
  {
    code: 'rider',
    name: 'Rider',
    group: 'Delivery',
    // No delivery.view (Delivery D2, CD-97): that reads every delivery in the organisation. A rider reads only their own
    // round, through the api.delivery_* functions that start from the caller. delivery.qr.bypass: skip-verify at a door.
    description: 'Delivery person. Uses the DESIGO® Fleet rider app; no ERP web access by default.',
    externalRoleKey: 'rider',
    requiresMfa: false,
    permissions: [...BASE, 'leave.request', 'delivery.record', 'delivery.qr.bypass'],
  },
  {
    code: 'customer_service',
    name: 'Customer service',
    group: 'Customers',
    description: 'Customers, subscriptions, orders, cases and conversations; no financial posting.',
    requiresMfa: false,
    permissions: [
      ...BASE, 'customer.view', 'customer.create', 'customer.edit', 'customer.pii.view', 'subscription.view',
      'subscription.manage', 'order.view', 'order.create', 'lead.view', 'lead.manage', 'bottles.view', 'delivery.view',
      'customer.payment.view', 'service.case.view', 'service.case.manage', 'service.conversation.view', 'reports.view', 'customer.day.manage',
      'jobs.alerts.view', 'bulk.view',
    ],
  },
  {
    // 139000 (the owner, 2026-10-21: "Bhavika jaise staff ko CRM or automation ka full control"; after the cut-off she
    // changes a day directly; "poori settings bhi"). customer_service stays as it was.
    code: 'crm_manager',
    name: 'CRM manager',
    group: 'Customers',
    description: 'The whole CRM and Automation: customers, agreements, orders (also after the cut-off), payments, cases, conversations and the settings; no financial posting.',
    requiresMfa: false,
    permissions: [
      ...BASE, 'customer.view', 'customer.create', 'customer.edit', 'customer.pii.view', 'subscription.view',
      'subscription.manage', 'order.view', 'order.create', 'lead.view', 'lead.manage', 'bottles.view', 'delivery.view',
      'customer.payment.view', 'service.case.view', 'service.case.manage', 'service.conversation.view', 'reports.view', 'customer.day.manage',
      'jobs.alerts.view', 'bulk.view',
      'customer.code.allocate', 'customer.discount.manage', 'customer.payment.create', 'customer.payment.mark', 'customer.status.manage',
      'customer.status.publish', 'customer.subscription.manage', 'subscription.cancel', 'order.edit', 'order.cancel', 'order.late_change',
      'finance.customer.adjust', 'config.manage', 'config.view', 'bottles.manage', 'bottles.custody.correct', 'delivery.exception.manage',
      'approvals.view', 'attention.manage', 'logistics.routes.view', 'reports.export', 'notifications.field_notice.send',
    ],
  },
  {
    code: 'accounts',
    name: 'Accounts',
    group: 'Finance',
    description: 'Invoices, payments, allocations, settlements preparation and expenses.',
    requiresMfa: true,
    permissions: [
      ...BASE, 'approvals.view', 'finance.view', 'finance.ledger.view', 'finance.invoice.issue', 'finance.expense.create',
      'finance.vendor.manage', 'customer.view', 'customer.payment.view', 'customer.payment.create', 'pricing.view',
      'farmer.settlement.view', 'farmer.settlement.prepare', 'bottles.view', 'reports.view', 'reports.export',
    ],
  },
  {
    code: 'finance_manager',
    name: 'Finance manager',
    group: 'Finance',
    description: 'Financial approvals, reversals, period close, cost centres.',
    requiresMfa: true,
    permissions: [
      ...BASE, 'attention.manage', 'approvals.view', 'finance.view', 'finance.ledger.view', 'finance.journal.post',
      'finance.invoice.issue', 'finance.payment.reverse', 'finance.charge.waive', 'finance.expense.approve',
      'finance.vendor.manage', 'finance.costcenter.manage', 'masterdata.view', 'masterdata.finance.manage',
      'finance.period.close', 'farmer.settlement.view',
      'farmer.settlement.approve', 'farmer.payment.create', 'payroll.view', 'payroll.approve', 'payroll.pay',
      'pricing.view', 'pricing.manage', 'customer.view', 'customer.payment.view', 'reports.view', 'reports.export',
      'analytics.view',
    ],
  },
  {
    code: 'hr_manager',
    name: 'HR manager',
    group: 'Workforce',
    description: 'People, employment, documents, attendance, leave, assignments, training, exits.',
    requiresMfa: true,
    permissions: [
      ...BASE, 'approvals.view', 'people.view', 'people.create', 'people.edit', 'people.salary.view', 'people.documents.view',
      'people.documents.manage', 'people.offer.issue', 'people.exit.manage', 'attendance.view', 'attendance.manage',
      'shifts.manage', 'leave.view', 'leave.approve', 'assignment.view', 'assignment.manage', 'tasks.view', 'tasks.manage',
      'training.view', 'training.manage', 'performance.view', 'performance.manage', 'reports.view',
    ],
  },
  {
    code: 'payroll_manager',
    name: 'Payroll manager',
    group: 'Workforce',
    description: 'Salary structures, incentives, earnings, payroll calculation (approval by finance).',
    requiresMfa: true,
    permissions: [
      ...BASE, 'approvals.view', 'people.view', 'people.salary.view', 'people.salary.edit', 'people.bank.view',
      'attendance.view', 'leave.view', 'performance.view', 'incentive.rules.manage', 'earnings.view', 'earnings.adjust',
      'payroll.view', 'payroll.calculate', 'reports.view', 'reports.export',
    ],
  },
  {
    code: 'auditor',
    name: 'Auditor (read-only)',
    group: 'Governance',
    description: 'Read-only across modules including audit; no sensitive reads unless separately granted.',
    externalRoleKey: 'ops_auditor',
    requiresMfa: true,
    permissions: [
      ...BASE, 'audit.view', 'approvals.view', 'config.view', 'system.logs.view', 'integration.view',
      ...ALL.filter((c) => /\.view$/.test(c) && !['farm.bill.view', 'people.salary.view', 'people.bank.view', 'people.documents.view', 'farm.locus.kyc.view', 'customer.pii.view', 'logistics.gps.view', 'service.conversation.view', 'audit.sensitive_access.view'].includes(c)),
      'reports.view',
    ],
  },
  {
    code: 'management',
    name: 'Management (read-only)',
    group: 'Governance',
    description: 'Control center, dashboards, analytics and reports.',
    requiresMfa: true,
    permissions: [...BASE, 'overview.view', 'analytics.view', 'reports.view', 'reports.export', 'approvals.view',
      ...viewsOf('farm', 'quality', 'plant', 'inventory', 'delivery', 'finance', 'customer', 'subscription', 'people', 'attendance', 'payroll', 'logistics')],
  },
  {
    code: 'integration_client',
    name: 'Integration connector (service)',
    group: 'Service',
    description: 'Service identity for Firebase connectors: ingest only.',
    requiresMfa: false,
    permissions: ['integration.ingest'],
  },
  {
    code: 'whatsapp_orchestrator',
    name: 'WhatsApp orchestrator (service)',
    group: 'Service',
    description: 'Service identity for the n8n WhatsApp automation: the WhatsApp API set only.',
    requiresMfa: false,
    permissions: ['service.whatsapp.api'],
  },
];

export function permissionsOfRole(code: string): ReadonlySet<string> {
  const role = ROLES.find((r) => r.code === code);
  return new Set(role ? role.permissions : []);
}
